/**
 * @-mentions in free text — the web half of `api/util/mentions.py`.
 *
 * A mention is stored inline in the text as `@[Display Name](user:<id>)`. This
 * module writes the tokens the API reads and reads the tokens the API writes,
 * so the two have to agree: a token this composes that the API can't parse is a
 * mention that silently notifies nobody, and a token the API stores that this
 * can't split is raw markup shown to a reader. The two test suites mirror each
 * other's cases — change them together.
 *
 * Two coordinate systems, and it matters which one you are in:
 *
 * - **markup** — what is stored and sent, tokens and all. This is the value the
 *   composer holds and the API receives.
 * - **display** — what a person reads and types, with each token shown as
 *   `@Display Name`. This is what a textarea's value and caret are in.
 *
 * `parse` is the bridge: it returns the pieces of a markup string with both
 * sets of offsets, so an edit made in display coordinates can be applied to the
 * markup underneath. Nobody should ever be shown the markup form.
 */

// Built fresh on each use: a shared /g regex carries `lastIndex` between calls,
// which would make the same text parse differently the second time.
const TOKEN = '@\\[([^\\]\\n]{1,100})\\]\\(user:([0-9a-fA-F]{24})\\)'

// Matches the API's MAX_MENTIONS. Composing more than this is possible; the
// server keeps the first 20, so the picker stops offering them at the same point.
export const MAX_MENTIONS = 20

// How far back from the caret an unfinished `@name` may run before we stop
// treating it as someone reaching for the picker.
const MAX_FRAGMENT = 50

function tokenPattern () {
  return new RegExp(TOKEN, 'g')
}

export function displayName (user) {
  if (!user) return 'Unknown'
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  return name || user.email || 'Unknown'
}

/** The token that mentions `user`, as the API expects to receive it. */
export function toToken (user) {
  return `@[${displayName(user)}](user:${user._id})`
}

/**
 * A markup string broken into its pieces, each carrying where it sits in both
 * coordinate systems: `{ type, value, markupStart, markupEnd, plainStart,
 * plainEnd }`, and for a mention its `id` and `name`.
 *
 * Everything else here is derived from this, so the plain text a reader sees,
 * the ids the API is sent, and the offsets an edit is applied at can't disagree
 * about where a mention begins and ends.
 */
export function parse (text) {
  const value = text || ''
  const parts = []
  const pattern = tokenPattern()
  let markupIndex = 0
  let plainIndex = 0
  let match

  function pushText (end) {
    if (end <= markupIndex) return
    const chunk = value.substring(markupIndex, end)
    parts.push({
      type: 'text',
      value: chunk,
      markupStart: markupIndex,
      markupEnd: end,
      plainStart: plainIndex,
      plainEnd: plainIndex + chunk.length
    })
    plainIndex += chunk.length
    markupIndex = end
  }

  while ((match = pattern.exec(value)) !== null) {
    pushText(match.index)
    const shown = `@${match[1]}`
    parts.push({
      type: 'mention',
      id: match[2].toLowerCase(),
      name: match[1],
      value: shown,
      markupStart: match.index,
      markupEnd: match.index + match[0].length,
      plainStart: plainIndex,
      plainEnd: plainIndex + shown.length
    })
    plainIndex += shown.length
    markupIndex = match.index + match[0].length
  }
  pushText(value.length)
  return parts
}

/** The ids mentioned in `text`, in the order they appear, deduped. */
export function extract (text) {
  const found = []
  for (const part of parse(text)) {
    if (part.type === 'mention' && !found.includes(part.id)) found.push(part.id)
  }
  return found.slice(0, MAX_MENTIONS)
}

/** `text` in display form: each token shown as `@Display Name`. What a reader
 * sees in a plain-text context, and what the composer's textarea holds. */
export function toPlainText (text) {
  return parse(text).map(part => part.value).join('')
}

/**
 * `text` split into the pieces a renderer needs: `{ type: 'text', value }` and
 * `{ type: 'mention', id, name }`, in order. Anything displaying comment or
 * note text must go through this, or the reader sees the markup.
 */
export function split (text) {
  return parse(text).map(part => (
    part.type === 'mention'
      ? { type: 'mention', id: part.id, name: part.name }
      : { type: 'text', value: part.value }
  ))
}

/** The markup offset a display offset falls at. A display offset inside a
 * mention's name resolves to the end of that mention, so an edit that reaches
 * into a name takes the whole mention with it. */
function toMarkupIndex (parts, plainIndex) {
  for (const part of parts) {
    if (plainIndex < part.plainStart) break
    if (plainIndex <= part.plainEnd) {
      if (part.type === 'mention') {
        return plainIndex <= part.plainStart ? part.markupStart : part.markupEnd
      }
      return part.markupStart + (plainIndex - part.plainStart)
    }
  }
  const last = parts[parts.length - 1]
  return last ? last.markupEnd : 0
}

/** The mention the caret is strictly inside, or null. Sitting in the middle of
 * a finished name is not someone reaching for the picker. */
export function mentionAt (text, plainIndex) {
  return parse(text).find(part => (
    part.type === 'mention' &&
    plainIndex > part.plainStart &&
    plainIndex < part.plainEnd
  )) || null
}

/**
 * What changed between two versions of the display text.
 *
 * A textarea only ever hands back the whole new string, so the edit has to be
 * recovered. Every ordinary edit — typing, pasting, backspace, forward delete,
 * replacing a selection — is one contiguous replacement that leaves the caret
 * at the end of whatever was inserted, so the caret pins the end of the change
 * exactly and the unchanged head and tail fall out from there.
 */
function changedRange (previous, next, caret) {
  const position = Math.max(0, Math.min(caret == null ? next.length : caret, next.length))
  const tailLength = next.length - position
  let end = Math.max(0, Math.min(previous.length, previous.length - tailLength))
  let start = 0
  const limit = Math.min(end, position)
  while (start < limit && previous[start] === next[start]) start++
  if (end < start) end = start
  return { start, end, inserted: next.substring(start, position) }
}

/**
 * Apply an edit made against the display text to the markup underneath.
 *
 * `nextPlain` and `caret` come straight off the textarea. Returns the new
 * markup and where the caret belongs in the new display text — the caller has
 * to put it back, because replacing a controlled textarea's value would
 * otherwise drop it at the end.
 *
 * An edit that reaches into a mention's name removes that mention outright: a
 * name with a letter missing is not a mention of anyone, and leaving a token
 * whose name no longer matches its id would show one person's name and notify
 * another. So backspacing at the end of a name deletes the whole name, which is
 * also how people expect a mention to behave.
 */
export function applyDisplayEdit (markup, nextPlain, caret) {
  const parts = parse(markup)
  const previousPlain = parts.map(part => part.value).join('')
  const { start, end, inserted } = changedRange(previousPlain, nextPlain, caret)
  let from = start
  let to = end
  for (const part of parts) {
    // Overlap, not touching: an edit that begins exactly where a mention ends
    // (or ends exactly where one begins) leaves it alone.
    if (part.type === 'mention' && part.plainStart < to && part.plainEnd > from) {
      from = Math.min(from, part.plainStart)
      to = Math.max(to, part.plainEnd)
    }
  }
  return {
    text: markup.substring(0, toMarkupIndex(parts, from)) +
      inserted +
      markup.substring(toMarkupIndex(parts, to)),
    caret: from + inserted.length
  }
}

/**
 * The unfinished `@name` the caret is sitting in, or null.
 *
 * Takes display text, as the composer sees it. Returns `{ start, query }` —
 * where the `@` is, and what has been typed after it — so the composer can
 * offer matching people and know what to replace. A mention only starts at the
 * beginning of a word (so an email address isn't one) and never runs past a
 * space or a line break.
 */
export function findActiveMention (text, caret) {
  const value = text || ''
  const position = Math.max(0, Math.min(caret == null ? value.length : caret, value.length))
  const start = value.lastIndexOf('@', position - 1)
  if (start < 0 || position - start > MAX_FRAGMENT) return null
  // Mid-word, so it's an email address rather than a mention.
  if (start > 0 && /[^\s(]/.test(value.charAt(start - 1))) return null
  const fragment = value.substring(start + 1, position)
  if (/[\s[\]()]/.test(fragment)) return null
  return { start, query: fragment }
}

/**
 * `markup` with the unfinished mention between the display offsets `start` and
 * `caret` replaced by a token for `user`.
 *
 * Returns the new markup and the caret's new display offset, which lands after
 * the trailing space so typing simply continues.
 */
export function applyMention (markup, start, caret, user) {
  const parts = parse(markup)
  const plain = parts.map(part => part.value).join('')
  // A trailing space so typing simply continues — unless the words after the
  // caret already start with one, which would leave a gap in the sentence.
  const spacer = /^\s/.test(plain.substring(caret)) ? '' : ' '
  const text = markup.substring(0, toMarkupIndex(parts, start)) +
    toToken(user) + spacer +
    markup.substring(toMarkupIndex(parts, caret))
  return { text, caret: start + `@${displayName(user)}${spacer}`.length }
}
